import { ComponentProps, useEffect, useRef } from "react";
import { DialogRoot } from "shared/components/Spor";

const IGNORE_DURATION_MS = 150;

type OnInteractOutside = NonNullable<
  ComponentProps<typeof DialogRoot>["onInteractOutside"]
>;

/**
 * A dialog can receive a stray "interact outside" event right after mounting,
 * caused by a *previous* dialog's focus-trap returning focus as it tears down
 * (e.g. when navigating between steps in a wizard where each step renders its
 * own `DialogRoot`). Spread the returned `onInteractOutside` onto `DialogRoot`
 * to suppress interactions in that short window, while leaving normal
 * outside-click behavior intact afterwards.
 */
export const useIgnoreInitialInteractOutside = (): OnInteractOutside => {
  const justMountedRef = useRef(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      justMountedRef.current = false;
    }, IGNORE_DURATION_MS);
    return () => clearTimeout(timer);
  }, []);

  return (e) => {
    if (justMountedRef.current) e.preventDefault();
  };
};
